import { Transformer } from '../types/transformer';
import { BlogCategory } from '../data/blogCategories';
import { BlogPost, BlogSummary } from '../types/BlogPost';

export function generateSeoUrl(transformer: Transformer): string {
  // Prefer the canonical stored slug — it is exactly what the detail page
  // (TransformerDetailPage) looks up, so the card link always resolves.
  if (transformer.slug && transformer.slug.trim() !== '') {
    return `/catalog/transformers/${transformer.slug}`;
  }

  // Fallback for legacy/mock data without a slug. Material compared
  // case-insensitively ("Медь"/"медь"); regex matches "/" and whitespace.
  const base = transformer.type.toLowerCase();
  const power = Math.round(Number(transformer.power));
  const voltage = transformer.voltage.replace('/', '-');
  const material = transformer.material.toLowerCase() === 'медь' ? 'cu' : 'al';
  const connection = transformer.connection
    ? `-${transformer.connection.replace(/[/\s]/g, '-')}`
    : '';

  return `/catalog/transformers/${base}-${power}-${voltage}-${material}${connection}`;
}

export const generateTransformerSchema = (transformer: Transformer) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: transformer.name,
    description: transformer.description,
    brand: {
      '@type': 'Brand',
      name: 'Проминнова',
    },
    offers: {
      '@type': 'Offer',

      priceCurrency: 'RUB',
      availability: 'https://schema.org/InStock',
    },
    weight: {
      '@type': 'QuantitativeValue',
      value: transformer.weight,
      unitText: 'kg',
    },
  };
};

interface SitemapData {
  staticPages: string[];
  transformers: Transformer[];
  blogPosts: BlogSummary[];
  blogCategories: BlogCategory[];
}

export const generateSitemap = (data: SitemapData) => {
  const baseUrl = import.meta.env.VITE_SITE_URL ?? 'https://transtsl.ru';

  const staticUrls = data.staticPages
    .map(
      (page) => `
    <url>
      <loc>${baseUrl}${page}</loc>
      <changefreq>weekly</changefreq>
      <priority>0.8</priority>
    </url>`
    )
    .join('');

  const transformerUrls = data.transformers
    .map(
      (transformer) => `
    <url>
      <loc>${baseUrl}${generateSeoUrl(transformer)}</loc>
      <changefreq>weekly</changefreq>
      <priority>0.7</priority>
    </url>`
    )
    .join('');

  const blogPostUrls = data.blogPosts
    .map(
      (post) => `
    <url>
      <loc>${baseUrl}/blog/${post.slug}</loc>
      <changefreq>monthly</changefreq>
      <priority>0.6</priority>
    </url>`
    )
    .join('');

  const blogCategoryUrls = data.blogCategories
    .map(
      (category) => `
    <url>
      <loc>${baseUrl}/blog/category/${category.slug}</loc>
      <changefreq>weekly</changefreq>
      <priority>0.7</priority>
    </url>`
    )
    .join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
    <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
      ${staticUrls}
      ${transformerUrls}
      ${blogPostUrls}
      ${blogCategoryUrls}
    </urlset>`;
};

export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://transtsl.ru';
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Проминнова',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+79137701139',
      contactType: 'customer service',
      areaServed: 'RU',
      availableLanguage: ['Russian'],
    },
    sameAs: ['https://vk.com/prominnova', 'https://t.me/prominnova'],
  };
};

export const generateBreadcrumbSchema = (pathnames: string[]) => {
  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://transtsl.ru';
  const items = pathnames.map((name, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: name.charAt(0).toUpperCase() + name.slice(1),
    item: `${siteUrl}/${pathnames.slice(0, index + 1).join('/')}`,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Главная',
        item: siteUrl,
      },
      ...items,
    ],
  };
};

export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

export const generateBlogPostingSchema = (post: BlogPost) => {
  const baseUrl = import.meta.env.VITE_SITE_URL ?? 'https://transtsl.ru';

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    image: post.image,
    url: `${baseUrl}/blog/${post.slug}`,
    datePublished: post.created_at,
    dateModified: post.created_at, // Assuming datePublished and dateModified are the same for now
    author: {
      '@type': 'Person',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Проминнова',
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/logo.png`,
      },
    },
    description: post.excerpt,
  };
};
